import React, { useMemo, Component } from 'react';
import { motion } from 'framer-motion';
import { Beaker } from 'lucide-react';
import { getEnabledModes } from '../modes';
import { LabStation } from './LabStation';
import { FamilyPicker } from './FamilyPicker';
import { ChallengeAcceptModal } from './modals/ChallengeAcceptModal';
import { ModeIntroSheet } from './modals/ModeIntroSheet';
import { FluencyModulePicker } from './modals/FluencyModulePicker';
import { PageContainer } from '../components/shared/PageContainer';
import { itemVariants } from '../components/shared/animations';
import { WorldZone } from '../components/shared/WorldZone';
import { useDjaySelection, getDjayLabWelcome } from '../mascot';
import { useAppStore } from '../app/hooks/useAppStore';
import { getFamilyMasteryKey } from '../mastery';
import { ConfettiCelebration } from '../accounts/components/ConfettiCelebration';
import { ContinueStrip } from './shared/ContinueStrip';
import { NewlyUnlockedBanner } from './shared/NewlyUnlockedBanner';
import { LabHero } from './shared/LabHero';
import { useLabPersonalization } from './hooks/useLabPersonalization';
import { useModeFlow, type StartModeConfig } from './hooks/useModeFlow';
import { useLabData } from './hooks/useLabData';
// ─── Types ────────────────────────────────────────────────────
interface LearningLabPageProps {
  onStartMode?: (modeId: string, config?: StartModeConfig) => void;
}
// ─── Component ────────────────────────────────────────────────
export function LearningLabPage({
  onStartMode
}: LearningLabPageProps) {
  const allModes = getEnabledModes();
  const {
    selectedVariant
  } = useDjaySelection();
  const {
    state,
    selectedGroup
  } = useAppStore();
  // ── Data Hook ──
  const {
    lastSession,
    newlyUnlockedFamilyId,
    setNewlyUnlockedFamilyId,
    showCelebration,
    personalRecords
  } = useLabData();
  const labWelcome = useMemo(() => getDjayLabWelcome(lastSession), [lastSession]);
  // ── Personalization Hook ──
  const {
    isFluencyColdStart,
    getEnrichmentForMode
  } = useLabPersonalization(personalRecords);
  // ── Flow Hook ──
  const {
    familyPickerOpen,
    familyPickerMode,
    challengeModalOpen,
    pendingChallengeFamilyId,
    introSheetMode,
    fluencyModulePickerOpen,
    handleModeClick,
    handleNewlyUnlockedClick,
    handleFamilySelect,
    handleFamilyPickerClose,
    handleChallengeAccept,
    handleChallengeDecline,
    handleIntroStart,
    handleIntroCancel,
    setFluencyModulePickerOpen
  } = useModeFlow({
    onStartMode,
    newlyUnlockedFamilyId
  });
  // ── Derived state ──
  const pendingChallengeMastery = pendingChallengeFamilyId ? state.familyMastery?.[getFamilyMasteryKey(pendingChallengeFamilyId, selectedGroup)]?.masteryScore ?? 0 : 0;
  // ── FamilyPicker full-screen view ──
  if (familyPickerOpen) {
    return <FamilyPicker onClose={handleFamilyPickerClose} onSelect={handleFamilySelect} mode={familyPickerMode} data-id="element-2935" data-mp="3h1pi7get3pw4" />;
  }
  // ── Main view ──
  const DAY_MS = 24 * 60 * 60 * 1000;
  return <PageContainer className="space-y-8 pb-20" data-id="element-2936" data-mp="1rn7rjql9ogad">
      {showCelebration && <ConfettiCelebration particleCount={100} duration={3000} data-id="element-2937" data-mp="1win0q58bxo0g" />}

      <NewlyUnlockedBanner familyId={newlyUnlockedFamilyId} onClick={handleNewlyUnlockedClick} onClose={() => setNewlyUnlockedFamilyId(null)} data-id="element-2938" data-mp="20ljmg4t6208m" />

      {/* Continue Strip */}
      {lastSession && Date.now() - lastSession.timestamp < 7 * DAY_MS && <motion.div variants={itemVariants} data-id="element-2939" data-mp="1cvpdvzfvqxu6">
          <ContinueStrip modeId={lastSession.modeId} familyId={lastSession.familyId} score={lastSession.score} timestamp={lastSession.timestamp} onContinue={() => {
        if (lastSession.familyId) {
          onStartMode?.(lastSession.modeId, {
            targetFamilyId: lastSession.familyId
          });
        } else {
          handleModeClick(lastSession.modeId);
        }
      }} data-id="element-2940" data-mp="1cwqt8g3xi6hp" />
        </motion.div>}

      {/* Compact Hero + Mission Area */}
      <motion.div variants={itemVariants} data-id="element-2941" data-mp="2rul7e7k64w36">
        <LabHero labWelcome={labWelcome} selectedVariant={selectedVariant} allModesLength={allModes.length} onModeClick={handleModeClick} data-id="element-2942" data-mp="2rrgwkrc7723j" />
      </motion.div>

      {/* Lab Stations Grid */}
      <motion.div variants={itemVariants} data-id="element-2943" data-mp="3fi33gfagugby">
        <WorldZone icon={Beaker} title="Hoe wil je oefenen?" accentColor="bg-fuchsia-100 text-fuchsia-600" djayMessage="Kies een manier om te oefenen! 🧪" djayEmotion="excited" isLast={true} data-id="element-2944" data-mp="3fkz265u5191g">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2945" data-mp="3kj0meu3aveei">
            {allModes.map(mode => {
            const enrichment = getEnrichmentForMode(mode.id);
            return <motion.div key={mode.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2 + allModes.indexOf(mode) * 0.05
            }} data-id="element-2946" data-mp="1q59crukz0uj2">
                  <LabStation mode={mode} statLabel={enrichment.statLabel} masteryPercent={enrichment.masteryPercent} personalRecord={enrichment.personalRecord} newlyUnlocked={mode.id === 'challenge' && !!newlyUnlockedFamilyId} onClick={() => handleModeClick(mode.id)} data-id="element-2947" data-mp="1b7q08k7f3nuu" />
                </motion.div>;
          })}
          </div>
        </WorldZone>
      </motion.div>

      {/* ChallengeAcceptModal */}
      {pendingChallengeFamilyId && <ChallengeAcceptModal isOpen={challengeModalOpen} familyId={pendingChallengeFamilyId} masteryScore={pendingChallengeMastery} onAccept={handleChallengeAccept} onDecline={handleChallengeDecline} data-id="element-2948" data-mp="2k9t4b3nhzhev" />}

      {/* Unified ModeIntroSheet */}
      {introSheetMode && <ModeIntroSheet isOpen={!!introSheetMode} modeId={introSheetMode} onStart={handleIntroStart} onCancel={handleIntroCancel} isColdStart={introSheetMode === 'fluency' ? isFluencyColdStart : false} data-id="element-2949" data-mp="26b1tbq3xmbuy" />}

      {/* FluencyModulePicker Modal */}
      <FluencyModulePicker isOpen={fluencyModulePickerOpen} onSelectAll={() => {
      setFluencyModulePickerOpen(false);
      onStartMode?.('fluency');
    }} onSelectModule={moduleId => {
      setFluencyModulePickerOpen(false);
      onStartMode?.('fluency', {
        targetModules: [moduleId]
      });
    }} onCancel={() => setFluencyModulePickerOpen(false)} data-id="element-2950" data-mp="2u64ki4q7krel" />
    </PageContainer>;
}